import type { RangeValue, FilterValues } from "@/services/filter";
import type { UrlFilterConfig } from "./types";
import { useRoute, useRouter } from "vue-router";

const DEFAULT_CONFIG: Required<UrlFilterConfig> = {
  excludeKeys: [],
  pageKey: "page",
  resetPageOnChange: true,
};

/**
 * Generic composable for managing filter state via URL query parameters.
 * URL is the source of truth - changes are made via router.replace() to avoid history spam.
 */
export function useUrlFilter(config: UrlFilterConfig = {}) {
  const route = useRoute();
  const router = useRouter();

  const { excludeKeys, pageKey, resetPageOnChange } = { ...DEFAULT_CONFIG, ...config };

  // Reactive filter state parsed from URL (type-agnostic)
  const filterValues = computed<FilterValues>(() => {
    const query = route.query;
    const values: FilterValues = {};

    for (const [key, value] of Object.entries(query)) {
      if (key === pageKey) continue;
      if (excludeKeys.includes(key)) continue;

      // Handle repeated params (multi-select): ?theme=a&theme=b -> ['a', 'b']
      if (Array.isArray(value)) {
        values[key] = value.filter((v): v is string => v !== null);
      } else if (value !== null) {
        values[key] = value;
      }
    }
    return values;
  });

  // Current page from URL (0-indexed)
  const page = computed(() => {
    const p = route.query[pageKey];
    return typeof p === "string" ? parseInt(p, 10) || 0 : 0;
  });

  // Set filter value (uses router.replace to avoid history spam)
  const setFilter = (field: string, value: string | string[] | null) => {
    const newQuery = { ...route.query };

    if (value === null || (Array.isArray(value) && value.length === 0) || value === "") {
      delete newQuery[field];
    } else {
      newQuery[field] = value;
    }

    // Reset to page 1 when filter changes
    if (resetPageOnChange) {
      delete newQuery[pageKey];
    }

    router.replace({ query: newQuery });
  };

  // Set multiple filters at once (single router.replace to avoid race conditions)
  const setFilters = (filters: Record<string, string | string[] | null>) => {
    const newQuery = { ...route.query };

    for (const [field, value] of Object.entries(filters)) {
      if (value === null || (Array.isArray(value) && value.length === 0) || value === "") {
        delete newQuery[field];
      } else {
        newQuery[field] = value;
      }
    }

    if (resetPageOnChange) {
      delete newQuery[pageKey];
    }

    router.replace({ query: newQuery });
  };

  // Set range filter (min/max)
  const setRangeFilter = (field: string, range: RangeValue) => {
    const newQuery = { ...route.query };

    if (range.min !== undefined) {
      newQuery[`${field}_min`] = String(range.min);
    } else {
      delete newQuery[`${field}_min`];
    }

    if (range.max !== undefined) {
      newQuery[`${field}_max`] = String(range.max);
    } else {
      delete newQuery[`${field}_max`];
    }

    if (resetPageOnChange) {
      delete newQuery[pageKey];
    }
    router.replace({ query: newQuery });
  };

  // Set page (doesn't reset filters, 0-indexed)
  const setPage = (newPage: number) => {
    if (newPage === 0) {
      const newQuery = { ...route.query };
      delete newQuery[pageKey];
      router.replace({ query: newQuery });
    } else {
      router.replace({ query: { ...route.query, [pageKey]: String(newPage) } });
    }
  };

  // Reset all filters (keeps other query params)
  const resetFilters = () => {
    router.replace({ query: {} });
  };

  return {
    filterValues,
    page,
    setFilter,
    setFilters,
    setRangeFilter,
    setPage,
    resetFilters,
  };
}
